/**
 * Internet Computer (ICP) API Service
 *
 * Provides access to ICP account data via the Rosetta API proxy.
 * Documentation: https://internetcomputer.org/docs/
 */

import { logger } from './logger';
import { callCoinGeckoAPI } from './api';
import type {
  ICPAccount,
  ICPTransaction,
  ICPNeuron,
  ICPWalletData,
  ICPNeuronSummary,
} from '../types/icp.types';

// ICP Rosetta API Proxy (to bypass CORS/CSP)
const ICP_PROXY_URL = '/api/icp/proxy';

// ICP Neurons API (NNS Governance Canister query)
const ICP_NEURONS_URL = '/api/icp/neurons';

// e8s to ICP conversion (1 ICP = 100,000,000 e8s)
const E8S_TO_ICP = 100_000_000;

// Request timeout in ms
const REQUEST_TIMEOUT_MS = 15000;

// Rate limiting
const REQUEST_DELAY_MS = 100;

/**
 * Delay helper for rate limiting
 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Call ICP Rosetta API via proxy
 */
const callIcpProxy = async (endpoint: string, body?: unknown): Promise<any> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(ICP_PROXY_URL, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ endpoint, body }),
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

/**
 * Call ICP Neurons API to fetch neuron data by Neuron IDs
 */
const callNeuronsApi = async (neuronIds: string[]): Promise<any> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS * 2);

  try {
    const response = await fetch(ICP_NEURONS_URL, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ neuron_ids: neuronIds }),
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

/**
 * Validate ICP Neuron ID format (numeric string)
 */
export const isValidNeuronId = (neuronId: string): boolean => {
  return /^\d+$/.test(neuronId.trim());
};

/**
 * Parse neuron state from dissolve_state
 */
const parseNeuronState = (dissolveState: any): 'locked' | 'dissolving' | 'dissolved' => {
  if (!dissolveState) return 'locked';

  // If WhenDissolvedTimestampSeconds exists, the neuron is dissolving
  if (dissolveState.WhenDissolvedTimestampSeconds) {
    const dissolveTime = parseInt(dissolveState.WhenDissolvedTimestampSeconds);
    const now = Math.floor(Date.now() / 1000);
    return dissolveTime <= now ? 'dissolved' : 'dissolving';
  }

  // If DissolveDelaySeconds exists and is > 0, it's locked
  if (dissolveState.DissolveDelaySeconds) {
    const delaySeconds = parseInt(dissolveState.DissolveDelaySeconds);
    return delaySeconds > 0 ? 'locked' : 'dissolved';
  }

  return 'locked';
};

/**
 * Parse neuron dissolve delay in seconds
 */
const parseDissolveDelay = (dissolveState: any): number => {
  if (!dissolveState) return 0;

  if (dissolveState.DissolveDelaySeconds) {
    return parseInt(dissolveState.DissolveDelaySeconds);
  }

  if (dissolveState.WhenDissolvedTimestampSeconds) {
    const dissolveTime = parseInt(dissolveState.WhenDissolvedTimestampSeconds);
    const now = Math.floor(Date.now() / 1000);
    return Math.max(0, dissolveTime - now);
  }

  return 0;
};

/**
 * Convert e8s to ICP
 */
export const e8sToIcp = (e8s: number): number => {
  return e8s / E8S_TO_ICP;
};

/**
 * Validate ICP account identifier format (64 hex characters)
 */
export const isValidIcpAccountId = (accountId: string): boolean => {
  return /^[a-f0-9]{64}$/.test(accountId.toLowerCase());
};

/**
 * Validate ICP principal format
 */
export const isValidIcpPrincipal = (principal: string): boolean => {
  // Principal format: xxxxx-xxxxx-xxxxx-xxxxx-xxx
  return /^[a-z0-9-]{5,}$/.test(principal.toLowerCase());
};

/**
 * ICP API Service
 */
export const icpAPI = {
  /**
   * Get account balance via Rosetta API proxy
   */
  async getAccountBalance(accountIdentifier: string): Promise<ICPAccount | null> {
    try {
      const data = await callIcpProxy('/account/balance', {
        network_identifier: {
          blockchain: 'Internet Computer',
          network: '00000000000000020101',
        },
        account_identifier: {
          address: accountIdentifier,
        },
      });

      const balance = data.balances?.[0]?.value || '0';

      return {
        accountIdentifier,
        balance: { e8s: parseInt(balance, 10) },
      };
    } catch (error: any) {
      // Account might not exist or have no balance
      if (error.message?.includes('404') || error.message?.includes('500')) {
        return {
          accountIdentifier,
          balance: { e8s: 0 },
        };
      }
      logger.error('IcpAPI', `Failed to fetch balance for ${accountIdentifier}`, error);
      return null;
    }
  },

  /**
   * Get account transactions via Rosetta API proxy
   */
  async getTransactions(
    accountIdentifier: string,
    limit: number = 50
  ): Promise<ICPTransaction[]> {
    try {
      const data = await callIcpProxy('/search/transactions', {
        network_identifier: {
          blockchain: 'Internet Computer',
          network: '00000000000000020101',
        },
        account_identifier: {
          address: accountIdentifier,
        },
        max_block: null,
        limit,
      });

      return data.transactions?.map((t: any) => t.transaction) || [];
    } catch (error) {
      logger.error('IcpAPI', `Failed to fetch transactions for ${accountIdentifier}`, error);
      return [];
    }
  },

  /**
   * Get neurons by specific neuron IDs (returns full API response)
   */
  async getNeuronsByIds(neuronIds: string[]): Promise<{ neurons: ICPNeuron[]; neuronInfos: any[]; networkStats: any }> {
    try {
      if (!neuronIds.length) return { neurons: [], neuronInfos: [], networkStats: null };

      const data = await callNeuronsApi(neuronIds);
      return {
        neurons: data.neurons || [],
        neuronInfos: data.neuron_infos || [],
        networkStats: data.network_stats || null,
      };
    } catch (error) {
      logger.error('IcpAPI', `Failed to fetch neurons by IDs`, error);
      return { neurons: [], neuronInfos: [], networkStats: null };
    }
  },

  /**
   * Get neuron summaries by neuron IDs (formatted for UI)
   */
  async getNeuronSummariesByIds(neuronIds: string[], icpPrice: number): Promise<{ summaries: ICPNeuronSummary[]; networkStats: any }> {
    try {
      if (!neuronIds.length) return { summaries: [], networkStats: null };

      const { neurons, neuronInfos, networkStats } = await this.getNeuronsByIds(neuronIds);

      // Create a map of neuron infos by ID for APY lookup
      const neuronInfoMap = new Map<string, any>();
      neuronInfos.forEach((info: any) => {
        neuronInfoMap.set(info.id, info);
      });

      const summaries = neurons.map((n: any) => {
        const neuronId = n.id?.[0]?.id || n.id?.id || '0';
        const neuronInfo = neuronInfoMap.get(neuronId.toString());

        const stakeE8s = parseInt(n.cached_neuron_stake_e8s || n.cachedNeuronStake?.e8s || '0');
        const maturityE8s = parseInt(n.maturity_e8s_equivalent || '0');
        const stakedMaturityE8s = parseInt(n.staked_maturity_e8s_equivalent?.[0] || '0');
        const stake = e8sToIcp(stakeE8s);
        const maturity = e8sToIcp(maturityE8s + stakedMaturityE8s);
        const dissolveState = n.dissolve_state?.[0] || n.dissolveState;
        const agingSince = parseInt(n.aging_since_timestamp_seconds || '0');
        const now = Math.floor(Date.now() / 1000);

        return {
          id: parseInt(neuronId),
          stake,
          stakeUsd: stake * icpPrice,
          maturity,
          dissolveDelay: parseDissolveDelay(dissolveState),
          state: parseNeuronState(dissolveState),
          votingPower: parseInt(n.voting_power || '0'),
          age: agingSince > 0 ? now - agingSince : 0,
          estimatedAPY: neuronInfo?.estimated_apy || 0,
        };
      });

      return { summaries, networkStats };
    } catch (error) {
      logger.error('IcpAPI', `Failed to get neuron summaries`, error);
      return { summaries: [], networkStats: null };
    }
  },

  /**
   * Get ICP price from CoinGecko
   */
  async getIcpPrice(): Promise<number> {
    try {
      const data = await callCoinGeckoAPI('/simple/price', {
        ids: 'internet-computer',
        vs_currencies: 'usd'
      });
      return data['internet-computer']?.usd || 0;
    } catch (error) {
      logger.error('IcpAPI', 'Failed to fetch ICP price', error);
      return 0;
    }
  },

  /**
   * Get complete wallet data for an account
   */
  async getWalletData(accountIdentifier: string, icpPrice: number): Promise<ICPWalletData | null> {
    try {
      if (!isValidIcpAccountId(accountIdentifier)) {
        logger.warn('IcpAPI', `Invalid account ID format: ${accountIdentifier}`);
        return null;
      }

      // Fetch account balance
      const account = await this.getAccountBalance(accountIdentifier);
      if (!account) {
        return null;
      }

      await delay(REQUEST_DELAY_MS);

      // Fetch transactions
      const transactions = await this.getTransactions(accountIdentifier, 25);

      // Calculate balance
      const balance = e8sToIcp(account.balance.e8s);

      return {
        accountIdentifier,
        principal: '', // Would need to derive from account ID
        balance,
        balanceUsd: balance * icpPrice,
        neurons: [], // Would need NNS query
        totalStaked: 0,
        totalStakedUsd: 0,
        totalMaturity: 0,
        totalMaturityUsd: 0,
        totalVotingPower: 0,
        recentTransactions: transactions,
        lastUpdated: Date.now(),
      };
    } catch (error) {
      logger.error('IcpAPI', `Failed to get wallet data for ${accountIdentifier}`, error);
      return null;
    }
  },

  /**
   * Get complete wallet data for a principal (includes neurons)
   */
  async getWalletDataByPrincipal(
    principal: string,
    accountIdentifier: string,
    icpPrice: number
  ): Promise<ICPWalletData | null> {
    try {
      // Fetch account balance if account ID provided
      let balance = 0;
      let transactions: ICPTransaction[] = [];

      if (accountIdentifier && isValidIcpAccountId(accountIdentifier)) {
        const account = await this.getAccountBalance(accountIdentifier);
        if (account) {
          balance = e8sToIcp(account.balance.e8s);
        }
        await delay(REQUEST_DELAY_MS);
        transactions = await this.getTransactions(accountIdentifier, 25);
      }

      await delay(REQUEST_DELAY_MS);

      // Fetch neurons for this principal
      const neurons = await this.getNeuronSummaries(principal, icpPrice);

      // Calculate totals from neurons
      const totalStaked = neurons.reduce((sum, n) => sum + n.stake, 0);
      const totalMaturity = neurons.reduce((sum, n) => sum + n.maturity, 0);
      const totalVotingPower = neurons.reduce((sum, n) => sum + n.votingPower, 0);

      return {
        accountIdentifier: accountIdentifier || '',
        principal,
        balance,
        balanceUsd: balance * icpPrice,
        neurons,
        totalStaked,
        totalStakedUsd: totalStaked * icpPrice,
        totalMaturity,
        totalMaturityUsd: totalMaturity * icpPrice,
        totalVotingPower,
        recentTransactions: transactions,
        lastUpdated: Date.now(),
      };
    } catch (error) {
      logger.error('IcpAPI', `Failed to get wallet data for principal ${principal}`, error);
      return null;
    }
  },

  /**
   * Get aggregated data for multiple accounts
   */
  async getMultipleWalletsData(
    accountIdentifiers: string[],
    icpPrice: number
  ): Promise<ICPWalletData[]> {
    const results: ICPWalletData[] = [];

    for (const accountId of accountIdentifiers) {
      const walletData = await this.getWalletData(accountId, icpPrice);
      if (walletData) {
        results.push(walletData);
      }
      await delay(500);
    }

    return results;
  },

  /**
   * Get aggregated data for multiple principals
   */
  async getMultipleWalletsByPrincipal(
    principals: Array<{ principal: string; accountId?: string }>,
    icpPrice: number
  ): Promise<ICPWalletData[]> {
    const results: ICPWalletData[] = [];

    for (const { principal, accountId } of principals) {
      const walletData = await this.getWalletDataByPrincipal(principal, accountId || '', icpPrice);
      if (walletData) {
        results.push(walletData);
      }
      await delay(500);
    }

    return results;
  },

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestampNanos: number): Date {
    return new Date(timestampNanos / 1_000_000);
  },

  /**
   * Format dissolve delay for display
   */
  formatDissolveDelay(seconds: number): string {
    const days = Math.floor(seconds / 86400);
    const years = Math.floor(days / 365);
    const remainingDays = days % 365;

    if (years > 0) {
      return `${years}y ${remainingDays}d`;
    }
    return `${days}d`;
  }
};

export default icpAPI;
